import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SearchableProductDropdown } from './SearchableProductDropdown';
import { SubmissionHistory } from './SubmissionHistory';
import { SubmissionProgress } from './SubmissionProgress';
import { ProductSelectionField, FarmProduct } from './ProductSelectionField';
import { HarvestDateFields, HarvestSeasonProduct } from './HarvestDateFields';

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

interface CoordinatorFormData {
  action: 'farmer_registration' | 'farm_price';
  farmerName?: string;
  farmerPhone?: string;
  farmerRegion?: string;
  farmerKebelle?: string;
  farmerCity?: string;
  farmerArea?: string;
  farmerLandSize?: string;
  harvestStartDate?: string;
  harvestEndDate?: string;
  harvestSeasonProducts?: HarvestSeasonProduct[];
  products?: FarmProduct[];
  productName?: string;
  origin?: string;
  price?: string;
  region?: string;
  notes?: string;
}

interface SubmissionStep {
  id: string;
  label: string;
  status: 'pending' | 'loading' | 'success' | 'error';
}

interface CoordinatorFormProps {
  userProfile: UserProfile;
  formData: CoordinatorFormData;
  date: Date;
  isSubmitting: boolean;
  submissionSteps?: SubmissionStep[];
  onInputChange: (field: string, value: string) => void;
  onProductsChange: (products: FarmProduct[]) => void;
  onHarvestSeasonProductsChange: (products: HarvestSeasonProduct[]) => void;
  onDateChange: (date: Date) => void;
  onSubmit: () => void;
}

export const CoordinatorForm: React.FC<CoordinatorFormProps> = ({
  userProfile,
  formData,
  date,
  isSubmitting,
  submissionSteps = [],
  onInputChange,
  onProductsChange,
  onHarvestSeasonProductsChange,
  onDateChange,
  onSubmit
}) => {
  const ethiopianRegions = [
    'Addis Ababa', 'Afar', 'Amhara', 'Benishangul-Gumuz', 'Dire Dawa',
    'Gambela', 'Harari', 'Oromia', 'Sidama', 'SNNP', 'Somali', 'Tigray'
  ];

  const isFormValid = () => {
    if (formData.action === 'farmer_registration') {
      const hasBasicInfo = formData.farmerName && formData.farmerPhone && formData.farmerRegion;
      const hasHarvestSeasonProducts = formData.harvestSeasonProducts && formData.harvestSeasonProducts.length > 0 && 
        formData.harvestSeasonProducts.every(p => p.productName.trim() !== '');
      const hasAdditionalProducts = !formData.products || formData.products.length === 0 || 
        formData.products.every(p => p.productName.trim() !== '');
      return hasBasicInfo && hasHarvestSeasonProducts && hasAdditionalProducts;
    } else {
      return formData.productName && formData.price && formData.region;
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <Card className="max-w-lg mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">
            👋 Welcome, {userProfile.first_name}!
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Coordinator Portal
          </p>
        </CardHeader>
        <CardContent>
          <Tabs 
            value={formData.action} 
            onValueChange={(value) => onInputChange('action', value)}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger 
                value="farmer_registration"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=inactive]:text-muted-foreground hover:text-foreground transition-colors"
              >
                👨‍🌾 Register Farmer
              </TabsTrigger>
              <TabsTrigger 
                value="farm_price"
                className="data-[state=active]:bg-accent data-[state=active]:text-accent-foreground data-[state=inactive]:text-muted-foreground hover:text-foreground transition-colors"
              >
                💰 Farm Price
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="farmer_registration" className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label htmlFor="farmerName">Farmer Name *</Label>
                <Input
                  id="farmerName"
                  value={formData.farmerName || ''}
                  onChange={(e) => onInputChange('farmerName', e.target.value)}
                  placeholder="Enter farmer's full name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="farmerPhone">Phone Number *</Label>
                <Input
                  id="farmerPhone"
                  value={formData.farmerPhone || ''}
                  onChange={(e) => onInputChange('farmerPhone', e.target.value)}
                  placeholder="+251912345678"
                  type="tel"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="farmerRegion">Region *</Label>
                <Select 
                  value={formData.farmerRegion || ''} 
                  onValueChange={(value) => onInputChange('farmerRegion', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {ethiopianRegions.map((region) => (
                      <SelectItem key={region} value={region}>
                        {region}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="farmerKebelle">Kebelle</Label>
                <Input
                  id="farmerKebelle"
                  value={formData.farmerKebelle || ''}
                  onChange={(e) => onInputChange('farmerKebelle', e.target.value)}
                  placeholder="Enter kebelle"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="farmerCity">City</Label>
                <Input
                  id="farmerCity"
                  value={formData.farmerCity || ''}
                  onChange={(e) => onInputChange('farmerCity', e.target.value)}
                  placeholder="Enter city"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="farmerArea">Area</Label>
                <Input
                  id="farmerArea"
                  value={formData.farmerArea || ''}
                  onChange={(e) => onInputChange('farmerArea', e.target.value)}
                  placeholder="Enter area"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="farmerLandSize">Land Size (hectares)</Label>
                <Input
                  id="farmerLandSize"
                  value={formData.farmerLandSize || ''}
                  onChange={(e) => onInputChange('farmerLandSize', e.target.value)}
                  placeholder="e.g., 2.5"
                  type="number"
                  step="0.1"
                />
              </div>

              <HarvestDateFields
                harvestStartDate={formData.harvestStartDate}
                harvestEndDate={formData.harvestEndDate}
                harvestSeasonProducts={formData.harvestSeasonProducts}
                onStartDateChange={(date) => onInputChange('harvestStartDate', date)}
                onEndDateChange={(date) => onInputChange('harvestEndDate', date)}
                onHarvestSeasonProductsChange={onHarvestSeasonProductsChange}
              />

              <ProductSelectionField
                products={formData.products || []}
                onProductsChange={onProductsChange}
              />
            </TabsContent>

            <TabsContent value="farm_price" className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label htmlFor="productName">Product *</Label>
                <SearchableProductDropdown
                  value={formData.productName || ''}
                  onValueChange={(value) => onInputChange('productName', value)}
                  placeholder="Search and select product"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="origin">Origin</Label>
                <Input
                  id="origin"
                  value={formData.origin || ''}
                  onChange={(e) => onInputChange('origin', e.target.value)}
                  placeholder="e.g., Local, Import"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">Price (ETB/kg) *</Label>
                <Input
                  id="price"
                  value={formData.price || ''}
                  onChange={(e) => onInputChange('price', e.target.value)}
                  placeholder="e.g., 45.50"
                  type="number"
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="region">Region *</Label>
                <Select 
                  value={formData.region || ''} 
                  onValueChange={(value) => onInputChange('region', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {ethiopianRegions.map((region) => (
                      <SelectItem key={region} value={region}>
                        {region}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes || ''}
                  onChange={(e) => onInputChange('notes', e.target.value)}
                  placeholder="Additional notes about the price..."
                  rows={3}
                />
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-6 pt-4 border-t">
            <Button
              onClick={onSubmit}
              disabled={!isFormValid() || isSubmitting}
              className="w-full"
            >
              {isSubmitting ? 'Submitting...' : 
               formData.action === 'farmer_registration' ? 'Register Farmer' : 'Submit Price'}
            </Button>
          </div>

          <div className="mt-4 text-xs text-center text-muted-foreground">
            Date: {date.toLocaleDateString()}
          </div>
        </CardContent>
      </Card>

      {/* Submission Progress */}
      {submissionSteps.length > 0 && (
        <SubmissionProgress steps={submissionSteps} className="mt-4 max-w-lg mx-auto" />
      )}

      {/* Submission History */}
      <div className="max-w-lg mx-auto">
        <SubmissionHistory />
      </div>
    </div>
  );
};